import { ComponentProps, PropsWithChildren } from "react";

import { Currency } from "@bentolabs/kit/entities";
import {
  useAllowancesQueries,
  useERC20ApproveMutation,
} from "@bentolabs/kit/hooks";
import { twMerge } from "tailwind-merge";
import { Address, parseUnits } from "viem";
import { useAccount, useChainId } from "wagmi";

import { ConnectWalletButton } from "features/connect-wallet";
import { tellerConfig } from "shared/config/const";
import { Button } from "shared/ui/button";

type CurrencyWithValue = Currency & { value: string | undefined };

type Props = ComponentProps<typeof Button> & {
  spenderAddress?: Address;
  approvalCurrencies: CurrencyWithValue[] | undefined;
  onApproveSuccess: (params: { currency: Currency }) => void;
  onSubmit?: () => void;
};

export const SubmitButton = (props: PropsWithChildren<Props>) => {
  const { isConnected, chain } = useAccount();
  const chainId = useChainId();
  const {
    children,
    className,
    approvalCurrencies,
    spenderAddress = tellerConfig[chainId],
    onApproveSuccess,
    onSubmit,
    ...rest
  } = props;

  const approveMutation = useERC20ApproveMutation();

  const allowancesQueries = useAllowancesQueries({
    currencyAddresses: approvalCurrencies?.map(({ address }) => {
      return address;
    }),
    contractAddress: spenderAddress,
  });

  const index = approvalCurrencies?.findIndex((collateral, index) => {
    const allowanceQuery = allowancesQueries[index];

    if (!allowanceQuery.isSuccess) return false;

    const amount = parseUnits(collateral.value ?? "", collateral.decimals);

    return allowanceQuery.data < amount;
  });

  const approvalCurrency =
    typeof index === "number" && index >= 0 && approvalCurrencies
      ? approvalCurrencies[index]
      : undefined;

  const handleApproveClick = (currency: Currency) => {
    approveMutation.mutate(
      {
        contractAddress: spenderAddress,
        currency,
      },
      {
        onSuccess: (_, variables) => {
          onApproveSuccess(variables);
        },
      }
    );
  };

  const cls = twMerge("w-full py-4 text-lg", className);

  if (approvalCurrency) {
    return (
      <Button
        type="button"
        size="56"
        variant={rest.variant}
        className={cls}
        isLoading={approveMutation.isPending}
        onClick={() => handleApproveClick(approvalCurrency)}
      >
        Approve spending {approvalCurrency.symbol}
      </Button>
    );
  }

  if (!isConnected || !chain) {
    return <ConnectWalletButton size="56" className={cls} />;
  }

  return (
    <Button
      type="submit"
      size="56"
      {...rest}
      onClick={onSubmit}
      className={cls}
    >
      {children}
    </Button>
  );
};
